import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getTenants } from '../services/tenant.service';
import { useAuthStore } from '../store/authStore';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';
import { displayLabel, formatDate } from '../utils/format';
import TenantPicker from '../components/common/TenantPicker';

// Teammates sign in with a code sent to their email, so an admin never needs
// to invent or share a password. The account still needs one to exist; this
// one is random and never shown.
const unusedPassword = () => {
  const bytes = new Uint8Array(18);
  window.crypto.getRandomValues(bytes);
  return `${Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('')}Aa1!`;
};

const inviteMessage = ({ name, email }) => [
  `Hi${name ? ` ${name}` : ''},`,
  '',
  'You now have access to our Serviti workspace, where we see leads and conversations from our AI agent.',
  '',
  `Sign in at ${window.location.origin}/auth/login with ${email}. Serviti emails you a sign-in code, so there is no password to remember.`,
].join('\n');

const PLATFORM_TENANT_ID = '00000000-0000-0000-0000-000000000000';
const EMPTY_NEW_USER = {
  username: '',
  email: '',
  full_name: '',
  password: '',
  account_type: 'tenant_user',
  tenant_id: '',
};
const EMPTY_EXISTING_USER = {
  email: '',
  account_type: 'tenant_user',
  tenant_id: '',
};

const UserManagementPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const isSuperadmin = currentUser?.role === 'superadmin';
  const isTenantAdmin = Boolean(
    !isSuperadmin &&
    currentUser?.tenant_id &&
    (currentUser?.tenant_role === 'owner' || currentUser?.tenant_role === 'admin' || currentUser?.role === 'admin')
  );
  
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExistingModal, setShowExistingModal] = useState(false);
  // For tenant admins, auto-set their tenant_id
  const [newUser, setNewUser] = useState({
    ...EMPTY_NEW_USER,
    tenant_id: isTenantAdmin ? currentUser.tenant_id : '',
  });
  const [existingUser, setExistingUser] = useState({
    ...EMPTY_EXISTING_USER,
    tenant_id: isTenantAdmin ? currentUser.tenant_id : '',
  });
  const [selectedNewTenant, setSelectedNewTenant] = useState(null);
  const [selectedExistingTenant, setSelectedExistingTenant] = useState(null);
  const [addError, setAddError] = useState('');
  const [existingError, setExistingError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [invite, setInvite] = useState(null);
  const [openRowMenu, setOpenRowMenu] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editData, setEditData] = useState({ email: '', full_name: '', role: '' });
  const [editError, setEditError] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [confirmResetPassword, setConfirmResetPassword] = useState('');
  const [resetError, setResetError] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  useEffect(() => {
    fetchUsers();
    if (isSuperadmin) {
      fetchTenants();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.pathname.endsWith('/add')) {
      setShowAddModal(true);
    }
  }, [location.pathname]);

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddError('');
    setSelectedNewTenant(null);
    if (location.pathname.endsWith('/add')) {
      navigate('..', { replace: true, relative: 'path' });
    }
  };

  const fetchUsers = async () => {
    try {
      // Non-superadmins should not see superadmin users
      const params = isSuperadmin ? {} : { exclude_superadmins: true };
      const response = await api.get('/auth/users', { params });
      setUsers(response.data);
    } catch (error) {
      alert('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const data = await getTenants(null, { limit: 100, excludePlatform: true });
      setTenants(data || []);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const toggleUserActive = async (user) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    const label = user.is_active ? 'Deactivate' : 'Reactivate';
    if (!window.confirm(`${label} user "${user.username}"?\n\n${user.is_active ? 'They will not be able to log in until reactivated.' : 'They will be able to log in again.'}`)) {
      return;
    }

    try {
      await api.post(`/auth/users/${encodeURIComponent(user.username)}/${action}`);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const openResetPasswordModal = (user) => {
    setResetUser(user);
    setResetPassword('');
    setConfirmResetPassword('');
    setResetError('');
    setShowResetModal(true);
  };

  const resetUserPassword = async () => {
    if (!resetUser) return;
    if (resetPassword.length < 6) {
      setResetError('New password must be at least 6 characters');
      return;
    }
    if (resetPassword !== confirmResetPassword) {
      setResetError('Passwords do not match');
      return;
    }
    if (!window.confirm(`Reset password for "${resetUser.username}"?\n\nShare the new password securely with the user.`)) {
      return;
    }

    try {
      await api.post(`/auth/users/${encodeURIComponent(resetUser.username)}/reset-password`, {
        new_password: resetPassword,
      });
      setShowResetModal(false);
      setResetUser(null);
      setResetPassword('');
      setConfirmResetPassword('');
      setResetError('');
      alert(`Password reset for '${resetUser.username}'`);
    } catch (error) {
      setResetError(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Helper to get tenant name from tenant_id
  const getTenantName = (tenantId) => {
    if (!tenantId) return '-';
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : tenantId.substring(0, 8) + '...';
  };

  const rememberTenant = (tenant) => {
    if (!tenant?.id) return;
    setTenants((prev) => {
      const withoutDuplicate = prev.filter((item) => item.id !== tenant.id);
      return [tenant, ...withoutDuplicate].slice(0, 100);
    });
  };

  const formatApiError = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((item) => item?.msg || String(item)).join(', ');
    }
    return detail.message || fallback;
  };

  const openDeleteDialog = (username) => {
      setUserToDelete(username);
      setDeleteDialogOpen(true);
    };

    const deleteUser = async () => {
      if (!userToDelete) return;
      try {
        await api.delete(`/auth/users/${encodeURIComponent(userToDelete)}`);
        setDeleteDialogOpen(false);
        setUserToDelete(null);
        fetchUsers();
      } catch (error) {
        alert('Failed to delete user');
      }
    };

  const openEditModal = (user) => {
    setEditUser(user);
    setEditData({
      email: user.email || '',
      full_name: user.full_name || '',
      role: user.role || 'user',
    });
    setEditError('');
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    if (!editUser) return;
    try {
      const payload = isSuperadmin ? editData : { ...editData, role: undefined };
      await api.patch(`/auth/users/${editUser.id}`, payload);
      setShowEditModal(false);
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      setEditError(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const changeRole = async (username, newRole) => {
    try {
      await api.put(`/auth/users/${encodeURIComponent(username)}/role`, { new_role: newRole });
      fetchUsers();
    } catch (error) {
      alert('Failed to change role');
    }
  };

  const addUser = async () => {
    if (!newUser.email.trim()) {
      setAddError('Email is required');
      return;
    }
    if (isSuperadmin && !newUser.username.trim()) {
      setAddError('Username is required');
      return;
    }
    if (isSuperadmin && !newUser.password.trim()) {
      setAddError('Password is required');
      return;
    }
    if (!newUser.account_type) {
      setAddError('Account type is required');
      return;
    }
    // For tenant admins, always use their own tenant_id
    const tenantIdToUse = isSuperadmin ? (newUser.tenant_id || null) : currentUser.tenant_id;
    const accountType = isSuperadmin ? newUser.account_type : 'tenant_user';
    const roleMap = {
      tenant_user: { role: 'user', tenant_role: 'member' },
      tenant_admin: { role: 'admin', tenant_role: 'admin' },
      superadmin: { role: 'superadmin', tenant_role: 'owner' },
    };
    const mappedRole = roleMap[accountType];

    if (!mappedRole) {
      setAddError('Invalid account type');
      return;
    }
    if (accountType !== 'superadmin' && !tenantIdToUse) {
      setAddError('Tenant is required for tenant users and tenant admins');
      return;
    }
    if (accountType !== 'superadmin' && tenantIdToUse === PLATFORM_TENANT_ID) {
      setAddError('Select a customer tenant, not the platform tenant');
      return;
    }

    const payload = {
      username: isSuperadmin ? newUser.username : newUser.email.trim().toLowerCase(),
      email: newUser.email,
      full_name: newUser.full_name || null,
      password: isSuperadmin ? newUser.password : unusedPassword(),
      role: mappedRole.role,
      tenant_role: mappedRole.tenant_role,
      tenant_id: accountType === 'superadmin' ? null : tenantIdToUse
    };

    try {
      const response = await api.post('/auth/signup', payload);
      closeAddModal();
      setNewUser({ ...EMPTY_NEW_USER, tenant_id: isTenantAdmin ? currentUser.tenant_id : '' });
      setAddError('');
      fetchUsers();
      if (isSuperadmin) {
        alert(response.data?.message || `User '${newUser.username}' created successfully`);
      } else {
        setInvite({ name: newUser.full_name.trim(), email: newUser.email.trim() });
      }
    } catch (error) {
      setAddError(formatApiError(error.response?.data?.detail, 'Failed to add user'));
    }
  };

  const addExistingUser = async () => {
    if (!existingUser.email.trim()) {
      setExistingError('Email is required');
      return;
    }

    const tenantIdToUse = isSuperadmin ? (existingUser.tenant_id || null) : currentUser.tenant_id;
    const accountType = isSuperadmin ? existingUser.account_type : 'tenant_user';
    const roleMap = {
      tenant_user: 'user',
      tenant_admin: 'admin',
    };
    const mappedRole = roleMap[accountType];

    if (!mappedRole) {
      setExistingError('Invalid account type');
      return;
    }
    if (!tenantIdToUse) {
      setExistingError('Tenant is required');
      return;
    }
    if (tenantIdToUse === PLATFORM_TENANT_ID) {
      setExistingError('Select a customer tenant, not the platform tenant');
      return;
    }

    const payload = {
      email: existingUser.email,
      role: mappedRole,
      tenant_id: tenantIdToUse,
    };

    try {
      const response = await api.post('/auth/users/attach-existing', payload);
      closeExistingModal();
      fetchUsers();
      alert(response.data?.message || 'Existing user added to tenant');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.requires_confirmation) {
        const existingTenants = (detail.existing_tenants || [])
          .map((tenant) => tenant.tenant_name || tenant.tenant_slug || tenant.tenant_id)
          .filter(Boolean)
          .join(', ');
        const targetName = detail.target_tenant?.tenant_name || getTenantName(tenantIdToUse);
        const confirmMessage = [
          `Existing user: ${detail.email || existingUser.email}`,
          detail.username ? `Username: ${detail.username}` : '',
          existingTenants ? `Current tenant access: ${existingTenants}.` : 'Current tenant access: none found.',
          detail.already_member
            ? `This user already belongs to ${targetName}. Update their access to ${mappedRole === 'admin' ? 'Tenant Admin' : 'Tenant User'}?`
            : `Add the same login to ${targetName} as ${mappedRole === 'admin' ? 'Tenant Admin' : 'Tenant User'}?`,
          'They will continue using the same email/password.',
        ].filter(Boolean).join('\n\n');

        if (!window.confirm(confirmMessage)) {
          setExistingError('Existing user was not added');
          return;
        }

        try {
          const confirmedResponse = await api.post('/auth/users/attach-existing', {
            ...payload,
            confirm_existing_user: true,
          });
          closeExistingModal();
          fetchUsers();
          alert(confirmedResponse.data?.message || 'Existing user added to tenant');
        } catch (confirmError) {
          setExistingError(formatApiError(confirmError.response?.data?.detail, 'Failed to add existing user'));
        }
        return;
      }
      setExistingError(formatApiError(detail, 'Failed to add existing user'));
    }
  };

  const closeExistingModal = () => {
    setShowExistingModal(false);
    setExistingError('');
    setExistingUser({ ...EMPTY_EXISTING_USER, tenant_id: isTenantAdmin ? currentUser.tenant_id : '' });
    setSelectedExistingTenant(null);
  };

  if (loading) return <div>Loading...</div>;

  const q = searchTerm.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter((u) =>
        [u.username, u.email, u.full_name, u.role, u.is_active ? 'active' : 'inactive']
          .some((f) => (f || '').toLowerCase().includes(q))
      )
    : users;

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex flex-wrap gap-2 justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{isSuperadmin ? 'User Management' : 'Team members'}</h1>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowExistingModal(true)}
            className="bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 px-4 py-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 transition"
          >
            {isSuperadmin ? '+ Add Existing User' : 'Add someone who already uses Serviti'}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          >
            {isSuperadmin ? '+ Create New User' : 'Invite a teammate'}
          </button>
        </div>
      </div>

      {invite && (
        <div role="status" className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <p className="font-semibold text-green-900 dark:text-green-100">
            {invite.email} can now sign in. Send them this so they know how:
          </p>
          <pre className="mt-2 whitespace-pre-wrap rounded bg-white p-3 text-sm text-gray-800 dark:bg-gray-800 dark:text-gray-100">{inviteMessage(invite)}</pre>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href={`mailto:${encodeURIComponent(invite.email)}?subject=${encodeURIComponent('Your Serviti access')}&body=${encodeURIComponent(inviteMessage(invite))}`}
              className="inline-flex min-h-[44px] items-center rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Email the invite
            </a>
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(inviteMessage(invite))}
              className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100"
            >
              Copy message
            </button>
            <button
              type="button"
              onClick={() => setInvite(null)}
              className="inline-flex min-h-[44px] items-center px-3 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">{isSuperadmin ? 'Create New User' : 'Invite a teammate'}</h2>
            
            {addError && (
              <div className="mb-4 p-2 bg-red-100 dark:bg-red-900/40 border border-red-400 text-red-700 dark:text-red-400 rounded">
                {addError}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white"
                placeholder="Enter email"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isSuperadmin
                  ? 'This email is used for sign in and password reset.'
                  : 'They sign in with this email. Serviti sends them a code, so there is no password to share.'}
              </p>
            </div>

            {isSuperadmin && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username *</label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white"
                placeholder="Enter username"
              />
            </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
              <input
                type="text"
                value={newUser.full_name}
                onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white"
                placeholder="Enter full name (optional)"
              />
            </div>

            {isSuperadmin && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password *</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white"
                placeholder="Enter password"
              />
            </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Type *</label>
              <select
                value={newUser.account_type}
                onChange={(e) => {
                  if (e.target.value === 'superadmin') {
                    setSelectedNewTenant(null);
                  }
                  setNewUser({
                    ...newUser,
                    account_type: e.target.value,
                    tenant_id: e.target.value === 'superadmin' ? '' : newUser.tenant_id,
                  });
                }}
                disabled={!isSuperadmin}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white"
              >
                <option value="tenant_user">{isSuperadmin ? 'Tenant User' : 'Team member'}</option>
                {isSuperadmin && <option value="tenant_admin">Tenant Admin</option>}
                {isSuperadmin && <option value="superadmin">Super Admin</option>}
              </select>
            </div>
            
            {/* Tenant dropdown - only for superadmins */}
            {isSuperadmin && newUser.account_type !== 'superadmin' && (
              <div className="mb-4">
                <TenantPicker
                  value={newUser.tenant_id}
                  onChange={(tenantId) => setNewUser({ ...newUser, tenant_id: tenantId })}
                  selectedTenant={selectedNewTenant}
                  onTenantSelect={(tenant) => {
                    setSelectedNewTenant(tenant);
                    rememberTenant(tenant);
                  }}
                  label="Assign to Tenant"
                  required
                  activeOnly={true}
                  helperText="Search and confirm the exact customer tenant before creating this user."
                />
              </div>
            )}
            
            {/* Info for tenant admins - user will be added to their tenant */}
            {isTenantAdmin && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <span className="font-medium">Note:</span> This user will be added to your organization.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  closeAddModal();
                  setNewUser({ ...EMPTY_NEW_USER, tenant_id: isTenantAdmin ? currentUser.tenant_id : '' });
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={addUser}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {isSuperadmin ? 'Create User' : 'Add teammate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Existing User Modal */}
      {showExistingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Add Existing User</h2>

            {existingError && (
              <div className="mb-4 p-2 bg-red-100 dark:bg-red-900/40 border border-red-400 text-red-700 dark:text-red-400 rounded">
                {existingError}
              </div>
            )}

            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Add a person who already has a Serviti login to this tenant. Their password stays the same.
              </p>
              <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                Existing admins stay admins across tenants; existing users stay users.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Existing User Email *</label>
              <input
                type="email"
                value={existingUser.email}
                onChange={(e) => setExistingUser({ ...existingUser, email: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white"
                placeholder="Enter existing login email"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Access Type *</label>
              <select
                value={existingUser.account_type}
                onChange={(e) => setExistingUser({ ...existingUser, account_type: e.target.value })}
                disabled={!isSuperadmin}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white"
              >
                <option value="tenant_user">{isSuperadmin ? 'Tenant User' : 'Team member'}</option>
                {isSuperadmin && <option value="tenant_admin">Tenant Admin</option>}
              </select>
              {!isSuperadmin && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Tenant admins can add users only. Ask Serviti support to add another tenant admin.
                </p>
              )}
            </div>

            {isSuperadmin && (
              <div className="mb-4">
                <TenantPicker
                  value={existingUser.tenant_id}
                  onChange={(tenantId) => setExistingUser({ ...existingUser, tenant_id: tenantId })}
                  selectedTenant={selectedExistingTenant}
                  onTenantSelect={(tenant) => {
                    setSelectedExistingTenant(tenant);
                    rememberTenant(tenant);
                  }}
                  label="Add to Tenant"
                  required
                  activeOnly={true}
                  helperText="Search and confirm the exact customer tenant before adding access."
                />
              </div>
            )}

            {isTenantAdmin && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  This login will be added to your current organization.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={closeExistingModal}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={addExistingUser}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Existing User
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          placeholder="Search by name, email or role…"
          className="w-full sm:max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
      <table className="w-full table-auto border-collapse">
        <thead className="sticky top-0 z-10 bg-gray-200 dark:bg-gray-900">
          <tr>
            <th className="px-4 py-3 text-left text-gray-800 dark:text-gray-300 font-semibold">{isSuperadmin ? 'Username' : 'Name'}</th>
            <th className="hidden md:table-cell px-4 py-3 text-left text-gray-800 dark:text-gray-300 font-semibold">Email</th>
            <th className="hidden md:table-cell px-4 py-3 text-left text-gray-800 dark:text-gray-300 font-semibold">Full Name</th>
            <th className="px-4 py-3 text-left text-gray-800 dark:text-gray-300 font-semibold">Role</th>
            <th className="px-4 py-3 text-left text-gray-800 dark:text-gray-300 font-semibold">Status</th>
            {isSuperadmin && <th className="hidden md:table-cell px-4 py-3 text-left text-gray-800 dark:text-gray-300 font-semibold">Tenant</th>}
            <th className="hidden md:table-cell px-4 py-3 text-left text-gray-800 dark:text-gray-300 font-semibold">Created At</th>
            <th className="px-4 py-3 text-left text-gray-800 dark:text-gray-300 font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {filteredUsers.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE).map(user => {
            const isCurrentUser = user.username === currentUser?.username;
            return (
            <tr key={user.username} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">{isSuperadmin ? user.username : (user.full_name || user.email || user.username)}</td>
              <td className="hidden md:table-cell px-4 py-3 text-gray-700 dark:text-gray-300">{user.email || '-'}</td>
              <td className="hidden md:table-cell px-4 py-3 text-gray-700 dark:text-gray-300">{user.full_name || '-'}</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                <select
                  value={user.role}
                  onChange={(e) => changeRole(user.username, e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-white rounded"
                  disabled={!isSuperadmin || (user.role === 'superadmin' && !isSuperadmin)}
                >
                  <option value="user">{isSuperadmin ? 'User' : displayLabel('member')}</option>
                  {isSuperadmin && <option value="admin">Admin</option>}
                  {isSuperadmin && <option value="superadmin">Superadmin</option>}
                </select>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user.is_active
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              {isSuperadmin && (
                <td className="hidden md:table-cell px-4 py-3 text-gray-700 dark:text-gray-300">
                  <span className="text-sm">{getTenantName(user.tenant_id)}</span>
                </td>
              )}
              <td className="hidden md:table-cell px-4 py-3 text-gray-700 dark:text-gray-300 text-sm">{formatDate(user.created_at)}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => openEditModal(user)}
                    className="inline-flex h-8 min-w-[64px] items-center justify-center rounded-md border border-blue-200 bg-blue-50 px-3 text-xs font-medium text-blue-700 transition hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:bg-blue-900/50"
                  >
                    Edit
                  </button>
                  <div className="relative">
                    <button
                      type="button"
                      aria-haspopup="menu"
                      aria-expanded={openRowMenu === user.username}
                      onClick={() => setOpenRowMenu((cur) => (cur === user.username ? null : user.username))}
                      disabled={isCurrentUser}
                      className="inline-flex h-8 min-w-[64px] items-center justify-center rounded-md border border-gray-300 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-45 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      More
                    </button>
                    {openRowMenu === user.username && (
                      <div role="menu" className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setOpenRowMenu(null); openResetPasswordModal(user); }}
                          className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                          Reset password
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setOpenRowMenu(null); toggleUserActive(user); }}
                          className="block w-full px-4 py-2 text-left text-sm text-amber-800 hover:bg-amber-50 dark:text-amber-200 dark:hover:bg-gray-700"
                        >
                          {user.is_active ? 'Deactivate' : 'Reactivate'}
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setOpenRowMenu(null); openDeleteDialog(user.username); }}
                          className="block w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-gray-700"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {/* Pagination */}
      {filteredUsers.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 mt-4 rounded-b-lg">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * PAGE_SIZE >= filteredUsers.length}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Edit User: {editUser.username}</h2>

            {editError && (
              <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                {editError}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                value={editData.full_name}
                onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={editData.role}
                onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                disabled={!isSuperadmin}
                className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900"
              >
                <option value="user">User</option>
                {isSuperadmin && <option value="admin">Admin</option>}
                {isSuperadmin && <option value="superadmin">Superadmin</option>}
              </select>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowEditModal(false); setEditUser(null); setEditError(''); }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && resetUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              Reset Password: {resetUser.username}
            </h2>

            {resetError && (
              <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                {resetError}
              </div>
            )}

            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
              <p className="text-sm text-amber-800">
                This sets a new password immediately. Share it securely with the user.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Password *</label>
              <input
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white"
                placeholder="Enter new password"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm Password *</label>
              <input
                type="password"
                value={confirmResetPassword}
                onChange={(e) => setConfirmResetPassword(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white"
                placeholder="Confirm new password"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowResetModal(false);
                  setResetUser(null);
                  setResetPassword('');
                  setConfirmResetPassword('');
                  setResetError('');
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={resetUserPassword}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setUserToDelete(null); }}
        onConfirm={deleteUser}
        title="Delete User"
        entityName={userToDelete || ''}
        warningText="This will permanently remove the user account. They will no longer be able to log in."
        confirmButtonText="Delete User"
      />
    </div>
  );
};

export default UserManagementPage;
